<template>
  <PageLayoutWithFilter class="metric-page">
    <template slot="header">
      <div class="d-flex m-3 mx-lg-0">
        <Breadcrumb :list="breadcrumbList" />
        <GroupsTreeSelect class="ml-1" @selectItem="selectGroupItem" />
      </div>
    </template>
    <template slot="main">
      <div class="page-reports">
        <b-card class="metric-overview">
          <div class="metric-text" v-if="metric.label">
            <h2>{{ metric.label }}</h2>
            <p v-html="metric.text"></p>
          </div>
          <div
            class="bg-metric"
            :style="{ 'background-image': `url(${metric.image})` }"
          ></div>
        </b-card>
        <br /><br />
        <b-card :class="{ 'inactive-group-card': metric.inactive }">
          <header slot="header" v-if="metric.label">
            {{ resources.page.reports.engagement.metric.overallTitle }}
            <span class="medium">{{ metric.label }}</span>
            <span v-if="metric.group_name">
              &nbsp;({{ metric.group_name }})
            </span>
            <GroupsInactive v-if="metric.inactive" />
          </header>
          <div class="flex over-score">
            <Overall
              :score="metric.score"
              :reliabilityRate="metric.reliability_rate"
            />
            <HistoricChart :history="metric.history"></HistoricChart>
          </div>
          <div class="text-center" v-if="!metric.inactive">
            <OpenActionPlanButton
              :groupId="groupId"
              @click="openActionPlanModal"
            />
          </div>
        </b-card>

        <br /><br />

        <b-card :class="{ 'inactive-group-card': metric.inactive }">
          <header slot="header" v-if="submetrics">
            <b-row>
              <b-col md="10">
                {{
                  resources.page.reports.engagement.metric.submetricTitlePart1
                }}
                <span class="medium">{{ metric.label }}</span>
                {{
                  resources.page.reports.engagement.metric.submetricTitlePart2
                }}
                {{
                  resources.page.reports.engagement.metric.submetricTitlePart3
                }}
              </b-col>
              <b-col md="2" v-if="false">
                <div class="switch-questions">
                  <b-link
                    @click.prevent="switchQuestions(false)"
                    v-show="questionsStatus"
                  >
                    {{ resources.page.reports.engagement.metric.hideQuestions }}
                    <i
                      class="mdi mdi-comment-remove-outline"
                      aria-hidden="true"
                    >
                    </i>
                  </b-link>
                  <b-link
                    @click.prevent="switchQuestions(true)"
                    v-show="!questionsStatus"
                  >
                    {{ resources.page.reports.engagement.metric.showQuestions }}
                    <i
                      class="mdi mdi-comment-question-outline"
                      aria-hidden="true"
                    >
                    </i>
                  </b-link>
                </div>
              </b-col>
            </b-row>
          </header>
          <MetricList
            :key="metric.metric_id"
            :list="submetrics"
            :questionsStatus="questionsStatus"
            :canViewMetricsInfo="true"
          >
          </MetricList>
        </b-card>

        <br /><br />
        <b-card
          v-if="recommendations$ && recommendations$.length && !metric.inactive"
        >
          <header slot="header">
            {{ resources.page.reports.engagement.metric.recommendationTitle }}
            <span class="medium">{{ metric.label }}</span>
          </header>
          <RecommendationCarousel
            v-if="recommendations$ && recommendations$.length"
            :recommendations="recommendations$"
            @recommendationButtonClicked="recommendationButtonClicked"
          />
        </b-card>
      </div>
      <NewActionPlan
        :plan="{
          groupId,
          metric: {
            _id: metric.metric_id,
            label: metric.label,
            score: metric.score,
          },
        }"
      />
      <b-modal
        id="modal-unauthorized"
        :title="resources.page.reports.engagement.unauthorizedModal.title"
        cancel-variant="outline-primary"
        ref="modal-unauthorized"
        header-bg-variant="danger"
      >
        <p class="txt-20">
          {{ resources.page.reports.engagement.unauthorizedModal.message }}
        </p>
        <template v-slot:modal-footer>
          &nbsp;
        </template>
      </b-modal>
    </template>
    <template slot="filter-collapsed">
      <user-attributes-filter
        :collapsed="true"
        :groupOid="groupOid"
        @loadUserAttributeReport="loadUserAttributeReport"
      />
    </template>
    <template slot="filter">
      <user-attributes-filter
        :collapsed="false"
        :groupOid="groupOid"
        @loadUserAttributeReport="loadUserAttributeReport"
      />
    </template>
  </PageLayoutWithFilter>
</template>

<script>
import { getUser, isManager } from '@/service/user'
import { mapGetters } from 'vuex'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import GroupsTreeSelect from '@/components/Groups/GroupsTreeSelect.vue'
import HistoricChart from '@/components/Reports/HistoricChart.vue'
import MetricList from '@/components/Reports/MetricList.vue'
import Overall from '@/components/Charts/Overall.vue'
import PageLayoutWithFilter from '@/components/Home/PageLayoutWithFilter.vue'
import RecommendationCarousel from '@/components/RecommendationCarousel.vue'
import NewActionPlan from '@/components/ActionPlan/New.js'
import { from, iif, combineLatest, of } from 'rxjs'
import { pluck, filter, mergeMap } from 'rxjs/operators'
import { getRecommendations } from '@/service/recommendation'
import { APP_MODAL_OPEN, RECOMMENDATION } from '@/components/Modal/Enum'
import { ACTION_PLAN_EVENT_OPEN, TEMPLATES } from '@/components/ActionPlan/Enum'
import OpenActionPlanButton from '@/components/ActionPlan/OpenActionPlanButton.vue'
import GroupsInactive from '@/components/Groups/GroupsInactive.vue'
import UserAttributesFilter from '@/components/Reports/Filters/UserAttributesFilter.vue'

export default {
  components: {
    Breadcrumb,
    GroupsTreeSelect,
    HistoricChart,
    MetricList,
    Overall,
    PageLayoutWithFilter,
    RecommendationCarousel,
    NewActionPlan,
    OpenActionPlanButton,
    GroupsInactive,
    UserAttributesFilter,
  },

  data() {
    return {
      questionsStatus: true,
    }
  },

  computed: {
    submetrics: function() {
      return this.metric.submetrics ? this.metric.submetrics : []
    },

    totalSubmetrics: function() {
      return this.metric.submetrics ? this.metric.submetrics.length : 0
    },

    ...mapGetters('user', {
      groupList: 'groupList',
      selectedGroup: 'selectedGroup',
    }),

    ...mapGetters({
      selectedReport: 'userAttributes/selectedReport',
      metric: 'metric/metric',
      resources: 'resources',
    }),

    user() {
      return getUser()
    },

    breadcrumbList() {
      return [
        {
          label: this.resources.general.menu.report,
        },
        {
          label: this.resources.general.menu.engagement,
          link: {
            name: 'reports',
            params: { groupId: this.$route.params.groupId },
          },
        },
        {
          label: this.metric.label,
        },
      ]
    },

    groupId: function() {
      return this.$route.params.groupId
    },

    groupOid() {
      return this.groupId === 'company' ? this.user.companyId : this.groupId
    },

    metricId: function() {
      return this.$route.params.metricId
    },
  },
  methods: {
    loadUserAttributeReport(report) {
      this.$store.dispatch('metric/resetMetricState')
      this.$store.dispatch('metric/requestMetric', {
        groupId: report.id,
        metricId: this.metricId,
      })
    },
    switchQuestions(status) {
      this.questionsStatus = status
    },
    selectGroupItem(group) {
      this.$router.push({
        name: 'company.metric',
        params: { groupId: group._id, metricId: this.$route.params.metricId },
      })
      if (!isManager()) {
        this.$store.dispatch('userAttributes/loadUserAttributesReports', {
          groupId: this.groupOid,
          unselect: true,
        })
      }
      this.$store.dispatch('metric/resetMetricState')
      this.$store
        .dispatch('metric/requestMetric', this.$route.params)
        .catch(() => {
          this.$router.push({ name: 'reports', params: { groupId: group._id } })
        })
    },
    recommendationButtonClicked({ title, image, metrics, id }) {
      this.$root.$emit(APP_MODAL_OPEN, { modal: RECOMMENDATION })
      this.$store.dispatch('recommendation/initializePost', {
        title: title,
        image: image,
        metrics: metrics,
      })
      this.$store.dispatch('recommendation/getRecommendationById', id)
    },
    openActionPlanModal() {
      this.$root.$emit(ACTION_PLAN_EVENT_OPEN, TEMPLATES.LIST_ACTION_PLANS)
    },
  },

  beforeMount() {
    this.$store.dispatch('metric/resetMetricState')
  },

  created() {
    const selectedReport = this.selectedReport || false
    const isSelected =
      selectedReport && selectedReport.key.split('|')[0] === this.groupOid

    if (isSelected) {
      this.$router.push({
        name: 'company.metric',
        params: {
          groupId: this.groupId,
          metricId: this.metricId,
        },
      })
      return this.$store
        .dispatch('metric/requestMetric', {
          groupId: selectedReport.id,
          metricId: this.metricId,
        })
        .catch(error => {
          const { status } = error.response
          if (status === 401 || status === 402) {
            this.$refs['modal-unauthorized'].show()
          }
        })
    }

    const groupId = this.groupId
    const selectedGroup = this.selectedGroup
    if (selectedGroup && selectedGroup !== groupId) {
      this.$router.push({
        name: 'company.metric',
        params: {
          groupId: selectedGroup,
          metricId: this.metricId,
        },
      })
      return this.$store
        .dispatch('metric/requestMetric', {
          groupId: selectedGroup,
          metricId: this.metricId,
        })
        .catch(error => {
          const { status } = error.response
          if (status === 401 || status === 402) {
            this.$refs['modal-unauthorized'].show()
          }
        })
    }

    this.$store
      .dispatch('metric/requestMetric', this.$route.params)
      .catch(error => {
        const { status } = error.response
        if (status === 401 || status === 402) {
          this.$refs['modal-unauthorized'].show()
        }
      })
  },

  subscriptions() {
    const metric$ = this.$watchAsObservable('metric').pipe(pluck('newValue'))
    const metricId$ = metric$.pipe(
      pluck('metric_id'),
      filter(a => a)
    )
    const lowMetric$ = metric$.pipe(pluck('low_metric'))

    const recommendations$ = combineLatest(lowMetric$, metricId$).pipe(
      mergeMap(([lowMetric, metricId]) =>
        iif(() => lowMetric, from(getRecommendations(metricId)), of([]))
      )
    )

    return {
      recommendations$,
    }
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

header {
  .d-flex {
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }
}

button {
  .material-icons {
    vertical-align: baseline;
  }
  span {
    vertical-align: text-bottom;
  }
}

.over-score {
  @media (max-width: 767px) {
    flex-direction: column;
  }
}

.nav.header .nav-link {
  margin: 0rem !important;
}

.page-reports {
  margin-bottom: 40px;
}

.purple {
  background: map-get($theme-colors, 'purple00');
}

.metric-overview {
  background: map-get($theme-colors, 'purple00');
  color: white;
  padding: 0 68px;

  @media (max-width: 768px) {
    padding: 0 0px;
  }

  .card-body {
    background-image: url('/metrics/vector.svg');
    background-position: 90% 70%;
    background-repeat: no-repeat;
    background-size: 40%;
    display: flex;
    flex-direction: row;
    min-height: 300px;
    padding: 0rem;

    @media (max-width: 576px) {
      flex-direction: column;
      background-position: 50% 80%;
      background-size: 96%;
    }

    .metric-text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 32px 0px 32px 0px;
      width: 54%;

      @media (max-width: 768px) {
        padding: 32px 0px 32px 15px;
      }

      @media (max-width: 576px) {
        flex-direction: column;
        padding: 1rem;
        width: 100%;
        padding-bottom: 0;
      }

      h2 {
        font-weight: 500;
        margin-bottom: 1.4rem;
        font-size: 3rem;
      }

      p {
        color: white;
        font-size: 18px;
      }

      @media (max-width: 576px) {
        h2 {
          margin-bottom: 1rem;
          font-size: 2rem;
        }

        p {
          font-size: 14px;
        }
      }
    }

    .bg-metric {
      background-position: center center;
      background-repeat: no-repeat;
      background-size: contain;
      flex-grow: 1;
      margin: 0;
      min-height: 230px;
    }
  }
}

img {
  margin: 0 30px 15px;
}

h5 {
  font-weight: 500;
  font-size: 22px;
  margin-bottom: 12px;
}

p {
  color: map-get($theme-colors, 'gray03');
  font-size: 15px;
}

.card {
  .card-header {
    font-size: 21px;
  }
  @media (max-width: 575.98px) {
    // sm
    .card-body {
      padding: 0px;
      margin: 0px;
    }
  }

  .flex {
    display: flex;
    justify-content: space-around;
    align-items: center;

    @media (max-width: 767px) {
      justify-content: center;
    }
  }
}

.switch-questions {
  font-size: 10pt;
  text-align: right;
  margin-top: 5px;
  i {
    font-size: 14pt;
  }
}

.inactive-group-card {
  background: map-get($theme-colors, 'gray08') !important;
  .card-header {
    background: map-get($theme-colors, 'gray08') !important;
  }
}
</style>
<style>
.metric-page .metric-item {
  border: none !important;
}
</style>
